import { ContentCopyOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import CustomIconButton from '@/components/inputs/CustomIconButton'
import { useAlertContext } from '@/contexts/AlertContext'

type CopyToClipboardButtonProps = {
  /** Exact text written to the clipboard. */
  value: string
  /**
   * Already-translated action label, e.g. "Copy UUID to clipboard", used as both the tooltip and the
   * accessible name so that several copy buttons on one page stay distinguishable. Passed in whole
   * rather than composed from a noun here: interpolating a translated noun into a translated sentence
   * hands Lokalise translators a fragment they cannot inflect or reorder.
   */
  actionLabel: string
}

/**
 * Icon button that copies `value` to the clipboard and confirms the outcome with an alert.
 */
function CopyToClipboardButton({ value, actionLabel }: Readonly<CopyToClipboardButtonProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  const copyToClipboard = async () => {
    try {
      // In an insecure context `navigator.clipboard` is `undefined`. Reading it is harmless; it is the
      // `.writeText` access on `undefined` that throws a TypeError, synchronously rather than as a
      // rejection. It stays inside the try so that failure lands in the same alert as a rejected write.
      await navigator.clipboard.writeText(value)
    } catch {
      pushAlert({
        message: t('common.copy_to_clipboard.failure'),
        severity: 'error',
      })
      return
    }

    // Deliberately outside the try: the write has already succeeded, so a throw from the alert path
    // must not be reported to the user as a failed copy.
    pushAlert({
      message: t('common.copy_to_clipboard.success'),
      severity: 'success',
    })
  }

  return (
    <CustomIconButton
      Icon={ContentCopyOutlined}
      aria-label={actionLabel}
      size="small"
      tooltipProps={{ title: actionLabel }}
      onClick={copyToClipboard}
    />
  )
}

export default CopyToClipboardButton
